import { array } from 'fp-ts';
import { useSelector } from 'react-redux';
import { AssistantSmartTipsEntity } from '@mews-commander/data-access';
import { isCurrentEnterpriseSmartTipsEnabled } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import { SmartTip } from '@mews/b2b-ui';
import { lt } from '@mews/framework';
import { SkeletonCard, Stack } from '@optimus-web/core';
import { useAssistantSmartTipsQuery } from '../queries';
import { GuestDetailEmptyContent } from './components';
import { GuestOverview, GuestOverviewProps } from './GuestOverview';
import { LoyaltyOverview, LoyaltyOverviewProps } from './Loyalty';
import { ReservationsOverview, ReservationsOverviewProps } from './Reservations';

interface GuestDetailContentProps {
    guestId: string;
    guestSummary: (GuestOverviewProps & LoyaltyOverviewProps & ReservationsOverviewProps) | undefined;
}

export const GuestDetailContent = ({ guestId, guestSummary }: GuestDetailContentProps) => {
    const { data, isFetching, error } = useAssistantSmartTipsQuery({
        id: guestId,
        entity: AssistantSmartTipsEntity.Customer,
    });
    const isSmartTipsEnabled = useSelector(isCurrentEnterpriseSmartTipsEnabled);

    if (!guestSummary) {
        return null;
    }

    const { loyaltyMemberships, reservations, ...other } = guestSummary;

    const hasLoyaltyMemberships = array.isNonEmpty(loyaltyMemberships);
    const hasReservations = array.isNonEmpty(reservations);

    return (
        <Stack vertical spacing="300">
            <GuestOverview {...other} loyaltyMemberships={loyaltyMemberships} />
            {!isFetching && isSmartTipsEnabled && data ? (
                <SmartTip
                    label={data.tips ? lt('AssistantSmartTipsTitle') : lt('AssistantSmartTipsNotAvailableTitle')}
                    disclaimer={data.tips ? lt('AssistantSmartTipsDisclaimer') : null}
                    textMoreButton={lt('MoreInfo')}
                    textLessButton={lt('LessInfo')}
                >
                    {error
                        ? lt('AssistantSmartTipsErrorMessage')
                        : data.tips || lt('AssistantSmartTipsNotAvailableBody')}
                </SmartTip>
            ) : (
                <SkeletonCard width="100%" height="150px" />
            )}
            {hasLoyaltyMemberships ? <LoyaltyOverview loyaltyMemberships={loyaltyMemberships} /> : null}
            {hasReservations ? <ReservationsOverview reservations={reservations} /> : null}
            {!hasLoyaltyMemberships && !hasReservations ? <GuestDetailEmptyContent id={guestId} /> : null}
        </Stack>
    );
};
